import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TournamentService } from '../../services/tournament/tournament.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { Round } from '../../models';
import { DialogService } from '../../services/shared/dialog/dialog.service';

@Component({
  selector: 'app-tournament-close',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tournament-close.component.html',
  styleUrls: ['./tournament-close.component.scss']
})
export class TournamentCloseComponent implements OnInit {
  private readonly tournamentService = inject(TournamentService);
  private readonly loggingService = inject(LoggingService);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);

  // Signals
  selectedTournament = this.tournamentService.selectedTournamentSignal;
  rounds = signal<Round[]>([]);
  isLoading = signal(false);
  isClosing = signal(false);

  // Computed
  allRoundsOfficial = computed(() => {
    const roundsList = this.rounds();
    return roundsList.length > 0 && roundsList.every(r => r.official);
  });

  canClose = computed(() => {
    const tournament = this.selectedTournament();
    return tournament && !tournament.official && this.allRoundsOfficial();
  });

  ngOnInit(): void {
    const tournament = this.selectedTournament();

    if (!tournament) {
      this.toastr.error('Please select a tournament first', 'Error');
      this.router.navigate(['/tournaments']);
      return;
    }

    this.loadRounds();
  }

  loadRounds(): void {
    const tournament = this.selectedTournament();
    if (!tournament?.tournamentId) {
      return;
    }

    this.isLoading.set(true);

    this.tournamentService.getRounds(tournament.tournamentId).subscribe({
      next: (rounds) => {
        this.rounds.set(rounds);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.loggingService.error('TournamentCloseComponent', 'Error loading rounds:', error);
        this.toastr.error('Failed to load rounds', 'Error');
        this.isLoading.set(false);
      }
    });
  }

  async confirmCloseTournament(): Promise<void> {
    const confirmMessage = 
      'Closing this Tournament will mark the Tournament as Complete and Official.\n' +
      'It will also distribute and publish the purse.\n\n' +
      'Are you sure you want to proceed?';

    const confirmed = await this.dialogService.confirm(confirmMessage, {
      title: 'Close Tournament',
      confirmLabel: 'Close Tournament',
      cancelLabel: 'Cancel'
    });

    if (!confirmed) {
      return;
    }

    this.closeTournament();
  }

  closeTournament(): void {
    const tournament = this.selectedTournament();

    if (!tournament?.tournamentId) {
      return;
    }

    this.isClosing.set(true);

    this.tournamentService
      .tournamentsTournamentIdClosePost(tournament.tournamentId)
      .subscribe({
        next: (result: any) => {
          this.toastr.success(result.message || 'Tournament marked Official and Published.', 'Success');
          this.isClosing.set(false);

          // Refresh tournament list
          this.tournamentService.getAll().subscribe({
            next: () => {
              this.loggingService.info('TournamentCloseComponent', 'Tournaments refreshed after close');
              // Navigate back to tournament list
              this.router.navigate(['/tournaments']);
            }
          });
        },
        error: (error: any) => {
          this.loggingService.error('TournamentCloseComponent', 'Error closing tournament:', error);
          this.toastr.error(error.error?.message || 'Failed to close tournament', 'Error');
          this.isClosing.set(false);
        }
      });
  }

  getRoundStatusClass(round: Round): string {
    if (round.official) return 'badge badge-primary';
    if (round.active) return 'badge badge-info';
    return 'badge badge-secondary';
  }

  getRoundStatusText(round: Round): string {
    if (round.official) return 'Official';
    if (round.active) return 'Active';
    return 'Not Started';
  }
}
